import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, BookOpen, AlertCircle } from 'lucide-react';
interface RuleCardProps {
  title: string;
  content: string;
  icon?: 'lightbulb' | 'book' | 'alert';
  label?: string;
  mode?: 'stimulus' | 'support';
}
const STYLE_MAP = {
  lightbulb: {
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-700',
    titleColor: 'text-amber-900'
  },
  book: {
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
    titleColor: 'text-blue-900'
  },
  alert: {
    bg: 'bg-red-50',
    border: 'border-red-100',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-700',
    titleColor: 'text-red-900'
  }
};
export function RuleCard({
  title,
  content,
  icon = 'lightbulb',
  label,
  mode = 'support'
}: RuleCardProps) {
  const Icon = icon === 'lightbulb' ? Lightbulb : icon === 'book' ? BookOpen : AlertCircle;
  const isStimulus = mode === 'stimulus';
  // Defensive lookup: a misconfigured visual-aid payload can pass an icon
  // string that isn't in STYLE_MAP (e.g. 'info', 'star', or undefined when
  // an explicit `icon={undefined}` skips the default). Without a fallback
  // the JSX below crashes with "Cannot read properties of undefined
  // (reading 'bg')". Fall back to the lightbulb style — visually consistent
  // with the most common usage and the prop default.
  const style = STYLE_MAP[icon] ?? STYLE_MAP.lightbulb;
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`${style.bg} rounded-xl border ${style.border} ${isStimulus ? 'p-3.5' : 'p-4'}`} data-id="element-13688" data-mp="2gffzk692sb2r">
      <div className="flex items-start gap-3" data-id="element-13689" data-mp="ak507jsq9gjz">
        <div className={`rounded-lg ${style.iconBg} flex items-center justify-center flex-shrink-0 ${isStimulus ? 'w-8 h-8' : 'w-10 h-10'}`} data-id="element-13690" data-mp="5mvby7h2raxq">
          <Icon className={`${style.iconColor} ${isStimulus ? 'w-4 h-4' : 'w-5 h-5'}`} data-id="element-13691" data-mp="cpjod7vrrykz" />
        </div>
        <div className="flex-1" data-id="element-13692" data-mp="mnzjdlxx3rzr">
          <h4 className={`font-bold ${style.titleColor} mb-1.5 ${isStimulus ? 'text-sm' : 'text-base'}`} data-id="element-13693" data-mp="mnpp3m5uayo9">
            {label || title}
          </h4>
          <p className={`text-slate-700 leading-relaxed whitespace-pre-line ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-13694" data-mp="3q4y6y60khyiz">
            {content}
          </p>
        </div>
      </div>
    </motion.div>;
}